// team-settings.js — Company Standards editor (Team page).
//
// Renders the full set of team-manageable coordination/governance
// settings as dynamic rows, each with a value control AND a per-row
// lock toggle. Locking a setting makes its team value an ENFORCED
// standard — member daemons must use it and can't override locally
// (see PR-B: teamSettingsOverrideFromTeamSync only enforces locked
// keys). Unlocked settings are not applied to any machine.
//
// Wire shape (PR-A): GET/PUT /v1/teams/settings
//   { version, values: {<key>: <typed value>}, locked_keys: [<key>] }
//
// Admin-only: non-admins see the values read-only with no save.
import { teamState } from "./page-state.js";
import { state } from "./state.js";
import { $ } from "./dom.js";
import { SETTING_SPECS as DAEMON_SPECS } from "./settings-schema.gen.js";
import { ago, escapeHTML as esc } from "./util.js";
import { api, apiWithMessage } from "./api.js";
import { memberName } from "./members.js";
import { activeBuId, activeBuName } from "./bu-switcher.js";

// buQuery builds the "?bu_id=<id>" suffix for the active workspace, or
// "" for "All workspaces" (the tenant default). Per-BU settings arc:
// the Settings page reads/writes the selected workspace's settings,
// falling back to the tenant default when none is selected.
function buQuery() {
  const id = activeBuId();
  return id ? "?bu_id=" + encodeURIComponent(id) : "";
}

// applySettingValue PUTs a single setting change WITHOUT clobbering the rest —
// it merges {key: value} onto the current teamState.settings.values and preserves
// locked_keys, then refreshes. Exported so the Protection-page recommendations
// can apply a suggested setting (e.g. turn on watch_unmanaged_writes_enabled)
// with one click, reusing the exact same PUT contract the Settings page uses.
// Returns the updated settings object; throws on failure so the caller can toast.
export async function applySettingValue(key, value) {
  const cur = teamState.settings || { values: {}, locked_keys: [] };
  const body = {
    values: Object.assign({}, cur.values, { [key]: value }),
    locked_keys: (cur.locked_keys || []).slice(),
  };
  const data = await apiWithMessage("/v1/teams/settings" + buQuery(), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  teamState.settings = data;
  pending = null;
  return data;
}

// currentSettingValue reads a setting's current value (or undefined if settings
// haven't loaded), so a recommendation can show the live value and only suggest
// a change that actually differs (and phrase "turn off" honestly).
export function currentSettingValue(key) {
  const s = teamState.settings;
  return s && s.values ? s.values[key] : undefined;
}

// hasTeamSettings reports whether the team settings have been loaded into
// teamState yet. Callers that gate behavior on a setting's value (e.g. the
// Protection recommendations) use this to avoid treating "not loaded" as a
// real value — currentSettingValue returns undefined in both cases.
export function hasTeamSettings() {
  return !!(teamState.settings && teamState.settings.values);
}

// Spec mirrors the coordinator's TEAM_SETTINGS_SPEC (coordinator.ts).
// Order here is the display order. `unit` is an optional suffix label.
// `def`, `min` and `max` come from the daemon's schema (below) for every key
// it has; `def` drives the default/custom pill and "Reset all to defaults".
// NOTE: team_settings defaults watch_unmanaged_writes_enabled to TRUE as of
// migration 048 (matching the local daemon, which always defaulted it on);
// require_identity defaults false.
const SETTINGS_SPEC = [
  { section: "Leases & coordination" },
  { key: "lease_ttl_secs", type: "int", unit: "seconds", label: "Lease TTL", desc: "How long a lease stays valid without a heartbeat before it's eligible for cleanup. Higher = safer for long crashes, lower = faster recovery of abandoned locks." },
  { key: "cooldown_secs", type: "int", unit: "seconds", label: "Cooldown duration", desc: "Wait after a lease is released before another chat can acquire it. Prevents two agents from ping-ponging the same file." },
  { key: "orphan_threshold_secs", type: "int", unit: "seconds", label: "Orphan threshold", desc: "How long a held lease can go without a heartbeat before it's reclaimed as orphaned (its owner is presumed gone)." },

  { section: "Enforcement & audit" },
  { key: "watch_unmanaged_writes_enabled", type: "bool", label: "Watch unmanaged writes", desc: "Detect writes to a leased file from outside the holding agent (e.g. manual edits, another tool). Detection only — records an audit event, does not block. On by default (migration 048)." },
  { key: "auto_install_pre_commit_hook", type: "bool", label: "Auto-install pre-commit hook", desc: "Install AC's pre-commit hook in managed repos so commits touching another agent's leased files are blocked — real teeth for tools without a hook layer (Aider, manual git, CI)." },
  { key: "track_reads", type: "bool", label: "Track reads", desc: "Record read operations (not just writes) in the audit trail. Useful for compliance; off reduces audit volume." },
  { key: "allow_revoke", type: "bool", label: "Allow revoke", desc: "Permit force-releasing another agent's lease. Off makes leases strictly owner-released." },
  { key: "require_identity", type: "bool", def: false, resetExempt: true, noLock: true, label: "Require sign-in to coordinate", desc: "When ON, a computer that hasn't run `agc login` can't coordinate with the team, whatever it uses: editor checks, the command line, the tool's AI asking AgentCollision directly, or git. This switch alone enforces it. 'Reset all to defaults' won't turn it off." },

  { section: "Git context" },
  { key: "git_context_enabled", type: "bool", label: "Git context enabled", desc: "Inject recent-commit context for the file an agent is about to edit, so it knows what changed there lately." },
  { key: "git_context_detail", type: "enum", values: ["minimal", "standard", "full"], label: "Git context detail", desc: "How much git history to surface: minimal (just the last touch), standard (recent commits), or full (commits + churn + PRs)." },
  { key: "git_context_lookback_hours", type: "int", unit: "hours", label: "Git context lookback", desc: "How far back in time to scan commits when building context." },
  { key: "git_context_max_commits", type: "int", unit: "commits", label: "Git context max commits", desc: "Cap on how many commits are surfaced, so the context block stays bounded." },
  { key: "git_context_churn_threshold", type: "int", unit: "edits", label: "Git context churn threshold", desc: "Edit count within the lookback window that flags a file as high-churn (a hot spot worth caution)." },
  { key: "git_context_timeout_secs", type: "int", unit: "seconds", label: "Git context timeout", desc: "Max time spent gathering git context before giving up, so a slow repo never stalls an edit." },
  { key: "git_context_include_prs", type: "bool", label: "Git context: include PRs", desc: "Include open pull-request info for the file in the context block." },
  { key: "git_context_include_worktrees", type: "bool", label: "Git context: include worktrees", desc: "Include sibling-worktree activity so an agent sees work happening in parallel checkouts." },

  { section: "Autonomous coordination" },
  { key: "autonomy_level", type: "enum", values: ["manual", "assist", "auto"], def: "", label: "Autonomy level", desc: "How much agents coordinate on their own. manual = detection only (humans decide every handoff); assist = agents flag conflicts but don't act; auto = agents reach out, negotiate, and hand off files end-to-end. Locking this enforces one level across every machine." },

  { section: "Observability" },
  { key: "log_level", type: "enum", values: ["debug", "info", "warn", "error"], label: "Log level", desc: "Daemon log verbosity. debug is noisy (for troubleshooting); error is quietest." },
  { key: "telemetry", type: "bool", label: "Telemetry", desc: "Send anonymous usage events to help improve AgentCollision. Off by default." },
];

// Defaults and limits come from the daemon's own schema, so the team page
// can't drift from what each computer actually uses (it once showed an
// orphan default of 120s against the daemon's 600s).
const DAEMON_SPEC_BY_KEY = {};
for (const d of DAEMON_SPECS) DAEMON_SPEC_BY_KEY[d.key] = d;
for (const s of SETTINGS_SPEC) {
  const d = s.key && DAEMON_SPEC_BY_KEY[s.key];
  if (!d) continue;
  s.def = d.default;
  if (d.min !== undefined) s.min = d.min;
  if (d.max !== undefined) s.max = d.max;
}

const SPEC_BY_KEY = {};
for (const s of SETTINGS_SPEC) if (s.key) SPEC_BY_KEY[s.key] = s;

// Pending edits live here until saved. Overlay on top of
// teamState.settings so render can show edited-but-unsaved values.
let pending = null; // { values: {}, locked: Set<string> } | null

function ensurePending() {
  if (pending) return pending;
  const s = teamState.settings || { values: {}, locked_keys: [] };
  pending = {
    values: Object.assign({}, s.values),
    locked: new Set(s.locked_keys || []),
  };
  return pending;
}

export async function fetchTeamSettings() {
  // A local daemon that isn't on a team has no team settings: its answer is
  // an empty stub, and rendering it showed "vundefined" and threw.
  if (state.teamInfo && state.teamInfo.solo) {
    teamState.settings = null;
    pending = null;
    const panel = $("team-settings-panel");
    if (panel) panel.style.display = "none";
    return;
  }
  try {
    const data = await api("/v1/teams/settings" + buQuery());
    teamState.settings = data;
    pending = null;
    renderTeamSettings();
  } catch (e) {
    const panel = $("team-settings-panel");
    if (panel) panel.style.display = "none";
  }
}

function isDirty() {
  if (!pending || !teamState.settings) return false;
  const s = teamState.settings;
  for (const k of Object.keys(pending.values)) {
    if (pending.values[k] !== s.values[k]) return true;
  }
  const orig = new Set(s.locked_keys || []);
  if (orig.size !== pending.locked.size) return true;
  for (const k of pending.locked) if (!orig.has(k)) return true;
  return false;
}

function controlHTML(spec, value, isAdmin) {
  const dis = isAdmin ? "" : "disabled";
  if (spec.type === "bool") {
    const checked = value ? "checked" : "";
    return `<input type="checkbox" class="ts-input" data-key="${spec.key}" ${checked} ${dis} />`;
  }
  if (spec.type === "enum") {
    const opts = spec.values
      .map((v) => `<option value="${v}" ${v === value ? "selected" : ""}>${esc(v)}</option>`)
      .join("");
    return `<select class="ts-input setting-select" data-key="${spec.key}" ${dis}>${opts}</select>`;
  }
  // int
  return `<input type="number" class="ts-input setting-number" data-key="${spec.key}" min="${spec.min}" max="${spec.max}" value="${esc(String(value))}" ${dis} />` +
    (spec.unit ? `<span style="color: var(--text-secondary); font-size: 13px; margin-left: 6px;">${esc(spec.unit)}</span>` : "");
}

// isDefaultValue reports whether v equals the setting's built-in default.
function isDefaultValue(spec, v) {
  if (spec.def === undefined) return true;
  if (spec.type === "bool") return !!v === !!spec.def;
  if (spec.type === "int") return Number(v) === Number(spec.def);
  return v === spec.def; // enum (string)
}

// default/custom pill — tells the user at a glance whether a value is the
// built-in default or something the team changed.
function pillHTML(spec, value) {
  const isDef = isDefaultValue(spec, value);
  const cls = isDef ? "ts-pill ts-pill-default" : "ts-pill ts-pill-custom";
  const label = isDef ? "default" : "custom";
  return `<span class="${cls}">${label}</span>`;
}

// `?` help tooltip on a setting's label. Reuses the dashboard's existing
// [data-tooltip] CSS pattern, so no new tooltip component is needed.
function helpHTML(desc) {
  return `<span class="ts-help" data-tooltip="${esc(desc)}" tabindex="0" role="img" aria-label="${esc(desc)}">?</span>`;
}

// A small lock toggle. Locked = enforced standard. data-lock carries
// the key; the click handler flips it in `pending.locked`.
function lockHTML(key, locked, isAdmin) {
  const title = locked
    ? "Enforced standard — members can't override. Click to unlock."
    : "Team default — members can override locally. Click to lock (enforce).";
  const cls = locked ? "ts-lock ts-lock-on" : "ts-lock";
  const glyph = locked ? "\u{1F512}" : "\u{1F513}"; // locked / unlocked
  const extra = isAdmin ? "" : "pointer-events:none;opacity:0.5;";
  return `<button class="${cls}" data-lock="${key}" title="${esc(title)}" style="${extra}">${glyph}</button>`;
}

export function renderTeamSettings() {
  const panel = $("team-settings-panel");
  if (!panel) return;
  if (!teamState.settings || !teamState.info) {
    panel.style.display = "none";
    return;
  }
  panel.style.display = "";

  const isAdmin = teamState.info.your_role === "admin";
  const { version, updated_at, updated_by, is_override } = teamState.settings;
  const p = ensurePending();

  const badge = $("team-settings-badge");
  if (badge) badge.textContent = "v" + version;

  // Per-workspace scope header: shows which workspace these settings
  // apply to, whether the values are inherited from the tenant default
  // or a workspace override, and (for an override) a "Reset to default"
  // action. Hidden when "All workspaces" is selected (= the default).
  const scopeEl = $("team-settings-scope");
  if (scopeEl) {
    const buId = activeBuId();
    if (!buId) {
      scopeEl.innerHTML =
        `<span class="ts-scope-label">Default — applies to every workspace unless overridden.</span>`;
    } else {
      const name = esc(activeBuName());
      const status = is_override
        ? `<span class="ts-scope-pill ts-scope-override">workspace override</span>`
        : `<span class="ts-scope-pill ts-scope-inherited">inherited from default</span>`;
      const resetBtn =
        isAdmin && is_override
          ? `<button class="btn btn-sm" id="team-settings-reset-bu" style="margin-left:auto;">Reset to default</button>`
          : "";
      scopeEl.innerHTML =
        `<span class="ts-scope-label">Workspace: <strong>${name}</strong></span> ${status} ${resetBtn}`;
    }
  }

  let html = "";
  for (const spec of SETTINGS_SPEC) {
    if (spec.section) {
      html += `<div class="ts-section">${esc(spec.section)}</div>`;
      continue;
    }
    const val = p.values[spec.key];
    const locked = p.locked.has(spec.key);
    // require_identity is enforced server-side by VALUE, not via the
    // locked_keys merge — so a lock toggle on it does nothing. Omit it
    // (noLock) rather than show a no-op control that implies the lock is
    // what enforces it.
    const lockCell = spec.noLock ? "" : lockHTML(spec.key, locked, isAdmin);
    html += `<div class="setting-row${locked ? " ts-row-locked" : ""}">
      <div class="setting-info">
        <div class="setting-label">${esc(spec.label)} ${helpHTML(spec.desc)} ${pillHTML(spec, val)}</div>
      </div>
      <div style="display:flex;align-items:center;gap:10px;">
        ${controlHTML(spec, val, isAdmin)}
        ${lockCell}
      </div>
    </div>`;
  }
  const rows = $("team-settings-rows");
  if (rows) rows.innerHTML = html;

  const dirty = isDirty();
  // Whether everything the reset button WOULD touch is already default.
  // Exclude resetExempt keys (require_identity) so the button doesn't
  // appear solely because of a setting it won't actually reset.
  const allDefault = SETTINGS_SPEC.every(
    (spec) => !spec.key || spec.resetExempt || isDefaultValue(spec, p.values[spec.key]),
  );
  const saveBtn = $("team-settings-save");
  const resetBtn = $("team-settings-reset");
  const defaultsBtn = $("team-settings-defaults");
  if (saveBtn) saveBtn.style.display = isAdmin && dirty ? "" : "none";
  if (resetBtn) resetBtn.style.display = isAdmin && dirty ? "" : "none";
  // "Reset all to defaults" shows for admins whenever something is not
  // already at its default (so it's actionable, not a no-op button).
  if (defaultsBtn) defaultsBtn.style.display = isAdmin && !allDefault ? "" : "none";

  const meta = $("team-settings-meta");
  if (meta) {
    if (dirty) {
      meta.innerHTML = '<span style="color: var(--warning); font-weight: 600;">Unsaved changes</span>';
    } else if (updated_by) {
      meta.textContent = "Last updated " + ago(updated_at) + " by " + memberName(updated_by);
    } else {
      meta.textContent = "Using default values. No standards enforced yet.";
    }
  }
}

function readControl(spec, el) {
  if (spec.type === "bool") return el.checked;
  if (spec.type === "enum") return el.value;
  return parseInt(el.value, 10);
}

function onInput(e) {
  const el = e.target.closest(".ts-input");
  if (!el) return;
  const key = el.getAttribute("data-key");
  const spec = SPEC_BY_KEY[key];
  if (!spec) return;
  ensurePending().values[key] = readControl(spec, el);
  renderTeamSettings();
}

function onLockClick(e) {
  const btn = e.target.closest(".ts-lock");
  if (!btn) return;
  const key = btn.getAttribute("data-lock");
  const p = ensurePending();
  if (p.locked.has(key)) p.locked.delete(key);
  else p.locked.add(key);
  renderTeamSettings();
}

// Discard unsaved edits — revert to the last-saved server state.
function resetSettingsInputs() {
  pending = null;
  renderTeamSettings();
}

// Reset every value to its built-in default (staged in `pending` — the
// admin still has to Save). Locks are left untouched: this resets the
// VALUES, not which settings are enforced. Becomes a normal pending edit
// (Save/Discard apply as usual).
function resetToDefaults() {
  const p = ensurePending();
  for (const spec of SETTINGS_SPEC) {
    if (!spec.key || spec.def === undefined) continue;
    // Never silently flip a security control off via a tidy-up button.
    // require_identity is coordinator-enforced by VALUE (not by lock),
    // so resetting it to its false default would disable team-wide
    // login enforcement with one unrelated click. Leave it as-is; the
    // admin must turn it off deliberately with its own toggle.
    if (spec.resetExempt) continue;
    p.values[spec.key] = spec.def;
  }
  renderTeamSettings();
}

async function saveTeamSettings() {
  const saveBtn = $("team-settings-save");
  if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = "Saving..."; }
  const toast = $("team-settings-toast");
  if (toast) toast.style.display = "none";

  const p = ensurePending();
  const body = {
    values: p.values,
    locked_keys: [...p.locked].sort(),
  };

  try {
    const data = await apiWithMessage("/v1/teams/settings" + buQuery(), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    teamState.settings = data;
    pending = null;
    renderTeamSettings();
    if (toast) {
      toast.style.display = "";
      const scope = activeBuId() ? `the "${activeBuName()}" workspace` : "all workspaces";
      toast.textContent = `Standards saved for ${scope}. Team daemons sync within 5 seconds.`;
      setTimeout(() => { toast.style.display = "none"; }, 4000);
    }
  } catch (e) {
    if (toast) {
      toast.style.display = "";
      toast.style.background = "var(--danger-bg)";
      toast.style.color = "var(--danger)";
      toast.textContent = "Failed: " + (e.message || "unknown error");
      setTimeout(() => {
        toast.style.display = "none";
        toast.style.background = "var(--success-bg)";
        toast.style.color = "var(--success)";
      }, 5000);
    }
  } finally {
    if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = "Save Changes"; }
  }
}

// resetWorkspaceToDefault deletes the active workspace's override so it
// inherits the tenant default again (per-BU settings arc). PUT
// {reset:true} with ?bu_id=. No-op when "All workspaces" is selected.
async function resetWorkspaceToDefault() {
  const id = activeBuId();
  if (!id) return;
  const toast = $("team-settings-toast");
  try {
    const data = await apiWithMessage("/v1/teams/settings?bu_id=" + encodeURIComponent(id), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reset: true }),
    });
    teamState.settings = data;
    pending = null;
    renderTeamSettings();
    if (toast) {
      toast.style.display = "";
      toast.textContent = `"${activeBuName()}" reset — now inherits the default. Daemons sync within 5 seconds.`;
      setTimeout(() => { toast.style.display = "none"; }, 4000);
    }
  } catch (e) {
    if (toast) {
      toast.style.display = "";
      toast.style.background = "var(--danger-bg)";
      toast.style.color = "var(--danger)";
      toast.textContent = "Reset failed: " + (e.message || "unknown error");
      setTimeout(() => {
        toast.style.display = "none";
        toast.style.background = "var(--success-bg)";
        toast.style.color = "var(--success)";
      }, 5000);
    }
  }
}

function wireListeners() {
  const rows = document.getElementById("team-settings-rows");
  if (rows) {
    rows.addEventListener("input", onInput);
    rows.addEventListener("change", onInput); // checkboxes/selects
    rows.addEventListener("click", onLockClick);
  }
  document.addEventListener("click", (e) => {
    if (e.target.id === "team-settings-save") saveTeamSettings();
    if (e.target.id === "team-settings-reset") resetSettingsInputs();
    if (e.target.id === "team-settings-defaults") resetToDefaults();
    if (e.target.id === "team-settings-reset-bu") resetWorkspaceToDefault();
  });
  // Re-fetch when the user switches workspace (per-BU settings arc) so
  // the Settings page reflects the newly-selected workspace's values.
  // Drops any unsaved edits — switching workspace is an explicit context
  // change, like navigating away.
  document.addEventListener("bu:changed", () => {
    pending = null;
    fetchTeamSettings();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
